<template>
    <b-container fluid class="p-4">
        <!-- 목록 카드 -->
        <b-card>
            <template #header>
                <div class="d-flex align-items-center justify-content-between">
                    <span class="font-weight-bold">스니펫 관리</span>
                    <b-button variant="success" size="sm" @click="openAddModal">
                        스니펫 추가
                    </b-button>
                </div>
            </template>

            <!-- 검색 툴바 -->
            <b-row class="mb-3">
                <b-col cols="12" md="6" class="mb-2 mb-md-0">
                    <b-form-input
                        v-model="searchFormValue.keyword"
                        placeholder="코드 또는 라벨로 검색"
                        size="sm"
                        :readonly="listLoading"
                        @keyup.enter="searchData"
                    />
                </b-col>
                <b-col cols="12" md="6" class="text-md-right">
                    <b-button variant="primary" size="sm" :disabled="listLoading" class="mr-1" @click="searchData">검색</b-button>
                    <b-button variant="outline-secondary" size="sm" :disabled="listLoading" @click="resetSearch">초기화</b-button>
                </b-col>
            </b-row>

            <b-table
                :items="snippets"
                :fields="columns"
                :busy="listLoading"
                show-empty
                responsive
                striped
                hover
            >
                <template #cell(index)="row">
                    {{ ((pagination.currentPage || 1) - 1) * pagination.perPage + row.index + 1 }}
                </template>

                <template #cell(detail)="row">
                    <b-button variant="outline-secondary" size="sm" @click="openDetailModal(row.item)">
                        상세
                    </b-button>
                </template>

                <template #cell(updated_at)="row">
                    {{ getDate(row.value) }}
                </template>

                <template #empty>
                    <PageEmptyComponent text="조회된 스니펫이 없습니다." :error="listError" @retry="fetchData" />
                </template>

                <template #table-busy>
                    <div class="text-center my-3">
                        <b-spinner variant="primary" />
                    </div>
                </template>
            </b-table>

            <div class="d-flex justify-content-center mt-3">
                <b-pagination
                    v-model="pagination.currentPage"
                    :total-rows="pagination.totalRows"
                    :per-page="pagination.perPage"
                    @change="(page) => changeQuery({ page })"
                />
            </div>
        </b-card>

        <!-- 스니펫 모달 -->
        <b-modal
            id="snippet-modal"
            :title="modalTitle"
            hide-footer
            no-close-on-backdrop
            size="xl"
            @hide="onModalHide"
            @hidden="onModalHidden"
        >
            <!-- add 모드 -->
            <template v-if="modalMode === 'add'">
                <b-form @submit.prevent>
                    <b-form-group
                        label="코드 *"
                        label-for="snippet-code"
                        description="영문·숫자·-·_ 권장. 공백과 / ? # % 는 쓸 수 없습니다(본문 참조 문법과 주소에 쓰임)."
                        :state="fieldState('code')"
                        :invalid-feedback="addErrors.code"
                    >
                        <b-form-input
                            id="snippet-code"
                            v-model="form.code"
                            placeholder="스니펫 코드 입력"
                            :state="fieldState('code')"
                        />
                    </b-form-group>

                    <b-form-group
                        label="버전 *"
                        label-for="snippet-version"
                        :state="fieldState('version')"
                        :invalid-feedback="addErrors.version"
                    >
                        <b-form-input
                            id="snippet-version"
                            v-model.number="form.version"
                            type="number"
                            min="1"
                            placeholder="버전 입력"
                            :state="fieldState('version')"
                        />
                    </b-form-group>

                    <b-form-group label="라벨" label-for="snippet-label">
                        <b-form-input
                            id="snippet-label"
                            v-model="form.label"
                            placeholder="라벨 입력"
                        />
                    </b-form-group>

                    <b-form-group label="설명" label-for="snippet-description">
                        <b-form-input
                            id="snippet-description"
                            v-model="form.description"
                            placeholder="설명 입력"
                        />
                    </b-form-group>

                    <b-form-group label="내용 *" :state="fieldState('content')" :invalid-feedback="addErrors.content">
                        <SnippetEditor v-model="form.content" />
                    </b-form-group>
                </b-form>

                <div class="d-flex justify-content-end mt-3">
                    <b-button
                        variant="primary"
                        :disabled="saving"
                        class="mr-2"
                        @click="saveSnippet"
                    >
                        추가
                    </b-button>
                    <b-button variant="secondary" :disabled="saving" @click="$bvModal.hide('snippet-modal')">
                        취소
                    </b-button>
                </div>
            </template>

            <!-- detail / edit 모드 -->
            <template v-else>
                <!-- 상단 메타 정보 -->
                <b-row class="mb-3">
                    <b-col cols="4">
                        <b-form-group label="코드">
                            <b-form-input :value="selectedSnippet && selectedSnippet.code" readonly />
                        </b-form-group>
                    </b-col>
                    <b-col cols="2">
                        <b-form-group label="버전">
                            <b-form-input :value="selectedSnippet && selectedSnippet.version" readonly />
                        </b-form-group>
                    </b-col>
                    <b-col cols="6">
                        <b-form-group label="라벨">
                            <b-form-input
                                v-model="form.label"
                                :readonly="modalMode === 'detail'"
                                placeholder="라벨"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row class="mb-3">
                    <b-col cols="12">
                        <b-form-group label="설명">
                            <b-form-input
                                v-model="form.description"
                                :readonly="modalMode === 'detail'"
                                placeholder="설명"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>

                <!-- 에디터 + 버전 타임라인 -->
                <b-row>
                    <b-col cols="8">
                        <div v-if="contentFailed" class="text-danger small mb-1">
                            내용을 불러오지 못했습니다. 다른 버전을 선택하거나 모달을 닫고 다시 열어주세요.
                        </div>
                        <SnippetEditor
                            v-model="form.content"
                            :readonly="modalMode === 'detail'"
                        />
                    </b-col>
                    <b-col cols="4">
                        <SnippetVersionTimeline
                            v-if="selectedSnippet"
                            :snippet-code="selectedSnippet.code"
                            :current-version="selectedSnippet.version"
                            @version-selected="onVersionSelected"
                            @version-added="fetchData"
                        />
                    </b-col>
                </b-row>

                <!-- 액션 버튼 -->
                <div class="d-flex justify-content-between mt-3">
                    <div>
                        <b-button
                            v-if="modalMode !== 'add'"
                            variant="outline-danger"
                            :disabled="saving || contentLoading || contentFailed"
                            @click="deleteSnippet"
                        >
                            삭제
                        </b-button>
                    </div>
                    <div>
                        <template v-if="modalMode === 'detail'">
                            <b-button variant="primary" :disabled="contentLoading || contentFailed"
                                      :title="contentFailed ? '내용을 불러오지 못해 수정할 수 없습니다. 모달을 닫고 다시 열어주세요.' : ''"
                                      @click="modalMode = 'edit'">
                                수정
                            </b-button>
                        </template>
                        <template v-else>
                            <b-button
                                variant="primary"
                                :disabled="saving"
                                class="mr-2"
                                @click="saveSnippet"
                            >
                                저장
                            </b-button>
                            <b-button variant="secondary" :disabled="saving" @click="$bvModal.hide('snippet-modal')">
                                취소
                            </b-button>
                        </template>
                    </div>
                </div>
            </template>
        </b-modal>
    </b-container>
</template>

<script>
import PaginationPageMixin from '@shared_mixins/pagination-page-mixins.js';
import unsavedChangesGuard from '@shared_mixins/unsaved-changes-guard.js';
import { columns } from './data.js';
import { getDefaultPagination, getDate } from '../../common-data.js';
import SnippetEditor from './_SubComponent/SnippetEditor.vue';
import SnippetVersionTimeline from './_SubComponent/SnippetVersionTimeline.vue';
import PageEmptyComponent from '@shared_components/admin/components/common/PageEmptyComponent.vue';
import { getErrorMessage } from '@core/utils/api/errorMessage';

// 목록 조회 조건 키(mixin). 상세 모달은 ?code=&version= (상세 보기만 — 추가·수정 모드는 URL 에 안 올림)
const LIST_KEYS = ['keyword', 'current_page', 'per_page'];
// 본문 참조 문법 {{% snippet 코드 버전 %}} 이 공백으로 나뉘고, 코드가 API 주소에 들어가므로 공백과 / ? # % 금지
const CODE_RE = /^[^\s/?#%]+$/;
const DISCARD_DIALOG = {
    title: '저장하지 않은 편집',
    message: '저장하지 않은 입력이 있습니다. 창을 닫으면 입력한 내용이 사라집니다. 닫으시겠습니까?',
};
const snippetPath = (code, version) => `/mail/snippet/${encodeURIComponent(code)}/${version}`;

export default {
    name: 'MailSnippet',

    components: {
        SnippetEditor,
        SnippetVersionTimeline,
        PageEmptyComponent,
    },

    mixins: [
        PaginationPageMixin({
            searchForm: { keyword: '' },
            pagination: getDefaultPagination(1, 20),
        }),
        // 모달에 저장 안 한 입력이 있으면 화면 이탈·새로고침 전에 확인
        unsavedChangesGuard,
    ],

    data() {
        return {
            snippets: [],
            listError: '',
            listSeq: 0,              // 목록 요청 순번 — 빠르게 페이지를 넘길 때 늦게 온 응답 무시
            modalMode: 'add',
            modalVisible: false,     // 모달이 열려 있거나 여는 중. 닫기가 확정되면(hide 통과) false
            skipHideGuard: false,    // 저장·삭제 성공, 확인을 마친 닫기는 다시 묻지 않는다
            hideConfirming: false,
            addSubmitted: false,     // 추가 폼은 처음 '추가'를 누른 뒤부터 칸별 오류 표시
            selectedSnippet: null,
            form: {
                code: '',
                version: 1,
                label: '',
                description: '',
                content: '',
            },
            saving: false,
            detailSeq: 0,            // 상세/버전 조회 요청 순번 — 늦게 온 이전 응답이 현재 폼을 덮지 않게
            contentLoading: false,   // 상세 content 조회 중/실패 — 빈 content 로 덮어쓰는 저장 방지
            contentFailed: false,
            columns,
        };
    },

    computed: {
        modalTitle() {
            if (this.modalMode === 'add') return '스니펫 추가';
            if (this.modalMode === 'edit') return '스니펫 수정';
            return '스니펫 상세';
        },
        // URL 의 상세 대상. version 은 1 이상 정수만
        routeDetail() {
            const { code, version } = this.$route.query;
            const v = typeof version === 'string' && /^\d+$/.test(version) ? Number(version) : 0;
            return typeof code === 'string' && code && v >= 1 ? { code, version: v } : null;
        },
        addErrors() {
            const f = this.form;
            const errors = {};
            if (!f.code) errors.code = '코드를 입력하세요.';
            else if (!CODE_RE.test(f.code)) errors.code = '공백과 / ? # % 는 쓸 수 없습니다.';
            if (!(Number.isInteger(f.version) && f.version >= 1)) errors.version = '1 이상의 정수를 입력하세요.';
            if (!(f.content || '').trim()) errors.content = '내용을 입력하세요.';
            return errors;
        },
        hasUnsavedChanges() {
            if (!this.modalVisible) return false;
            if (this.modalMode === 'add') {
                const f = this.form;
                return !!(f.code || f.label || f.description || f.content) || f.version !== 1;
            }
            return this.modalMode === 'edit' && this.isFormDirty();
        },
    },

    watch: {
        // 새로고침·뒤로가기·링크로 들어와도 검색어·페이지·상세가 주소대로
        '$route.query'(to, from) {
            if (this.$route.name !== 'MailSnippet') return;
            if (LIST_KEYS.some(k => to[k] !== from[k])) {
                this.setSearchDataFormQuery();
                this.fetchData();
            }
            // 상세 키가 그대로인 주소 변화(페이지 보정 등)로 닫히는 중인 모달을 다시 열지 않게
            if (['code', 'version'].some(k => to[k] !== from[k])) this.syncDetailFromRoute();
        },
    },

    mounted() {
        this.setSearchDataFormQuery();
        this.fetchData();
        this.syncDetailFromRoute();
    },

    // 뒤로가기 등으로 열린 상세가 바뀌거나 닫힐 때도 저장 안 한 수정이 있으면 확인
    beforeRouteUpdate(to, from, next) {
        const detailChanged = ['code', 'version'].some(k => to.query[k] !== from.query[k]);
        if (!detailChanged || !this.hasUnsavedChanges) return next();
        this.confirmDiscardChanges(DISCARD_DIALOG).catch(() => false).then(ok => next(ok ? undefined : false));
    },

    beforeDestroy() {
        this.listSeq++;
        this.detailSeq++;
    },

    methods: {
        getDate,

        async fetchData() {
            const seq = ++this.listSeq;
            this.listLoading = true;
            this.listError = '';
            try {
                // 검색어는 입력 초안이 아니라 적용된(URL) 값으로 — 검색을 누르지 않은 입력이 저장 뒤 조회에 섞이지 않게
                const { keyword } = this.$route.query;
                const { data } = await this.$axios.get('/mail/snippet', {
                    params: {
                        page: this.pagination.currentPage || 1,
                        size: this.pagination.perPage,
                        keyword: typeof keyword === 'string' && keyword ? keyword : undefined,
                    },
                });
                if (seq !== this.listSeq) return;
                const items = data.results || data.items || data;
                const total = data.total ?? items.length;
                // 오래된 링크나 마지막 행 삭제로 페이지가 마지막 페이지를 넘으면 마지막 페이지로 (자동 보정이라 replace, 감시가 다시 조회)
                const last = Math.max(1, Math.ceil(total / this.pagination.perPage));
                if (total > 0 && (this.pagination.currentPage || 1) > last) {
                    const query = { ...this.$route.query };
                    delete query.current_page;
                    if (last > 1) query.current_page = String(last);
                    this.$router.replace({ query }).catch(() => {});
                    return;
                }
                this.snippets = items;
                this.pagination.totalRows = total;
            } catch (e) {
                if (seq !== this.listSeq || e.response?.status === 401) return;
                // 실패를 '스니펫 없음'으로 보이지 않게 표 자리에 오류 + 다시 시도
                this.snippets = [];
                this.listError = getErrorMessage(e, '스니펫 목록을 불러오지 못했습니다.');
            } finally {
                if (seq === this.listSeq) this.listLoading = false;
            }
        },

        async searchData() {
            if (this.listLoading) return;
            this.pagination.currentPage = 1;
            if (await this.changeQuery({})) await this.fetchData();
        },

        openAddModal() {
            this.detailSeq++;
            this.contentFailed = false;
            this.contentLoading = false;
            this.resetSnippetForm();
            this.modalMode = 'add';
            this.openModal();
        },

        openModal() {
            this.skipHideGuard = false;
            this.modalVisible = true;
            this.$bvModal.show('snippet-modal');
        },

        // 상세 버튼: 모달을 열고 주소에 code·version 을 남긴다(push — 뒤로가기로 닫힘, 링크로 공유)
        openDetailModal(item) {
            this.showDetail(item.code, Number(item.version), item);
            this.$router.push({ query: { ...this.$route.query, code: item.code, version: String(item.version) } }).catch(() => {});
        },

        // 주소의 상세 대상과 모달을 맞춘다
        syncDetailFromRoute() {
            const target = this.routeDetail;
            const showing = this.modalVisible && this.modalMode !== 'add' && this.selectedSnippet;
            if (target) {
                if (showing && this.selectedSnippet.code === target.code && Number(this.selectedSnippet.version) === target.version) return;
                this.showDetail(target.code, target.version);
            } else if (showing) {
                // 확인은 beforeRouteUpdate 가 이미 했다
                this.closeModal();
            }
        },

        // hint: 목록·버전 타임라인 행(라벨 등을 조회 전에 미리 보여 준다). 링크로 열면 목록에서 찾는다.
        async showDetail(code, version, hint) {
            const seq = ++this.detailSeq;
            const base = hint || this.snippets.find(s => s.code === code && Number(s.version) === version) || {};
            this.selectedSnippet = { ...base, code, version };
            this.form = {
                code,
                version,
                label: base.label || '',
                description: base.description || '',
                content: '',
            };
            this.modalMode = 'detail';
            this.contentFailed = false;
            this.contentLoading = true;
            this.openModal();
            // 목록 API는 content·사용처를 포함하지 않으므로 상세 API로 fetch
            try {
                const { data } = await this.$axios.get(snippetPath(code, version));
                if (seq !== this.detailSeq) return;
                this.form.label = data.label || '';
                this.form.description = data.description || '';
                this.form.content = data.content || '';
                this.selectedSnippet = {
                    ...base, code, version,
                    label: data.label, description: data.description, content: data.content,
                    usage: this.usageOf(data),
                };
            } catch (e) {
                if (seq !== this.detailSeq || e.response?.status === 401) return;
                this.contentFailed = true;
                this.$EventBus.$emit('toast-failure', '오류', `${code} v${version} 내용을 불러오지 못했습니다. 수정할 수 없습니다.`);
            } finally {
                if (seq === this.detailSeq) this.contentLoading = false;
            }
        },

        // 이 스니펫이 바로 반영되는 곳 — 템플릿은 locale 별 행이 아니라 템플릿 수로 센다
        usageOf(data) {
            return {
                templates: new Set((data.usage_templates || []).map(t => t.template_id)).size,
                parents: data.usage_parent_snippet_count || 0,
            };
        },

        isFormDirty() {
            const cur = this.selectedSnippet;
            if (!cur) return false;
            return this.form.label !== (cur.label || '')
                || this.form.description !== (cur.description || '')
                || this.form.content !== (cur.content || '');
        },

        async onVersionSelected(v) {
            // 저장 중에 바꾸면 저장 완료가 새로 연 버전을 닫아 버린다
            if (this.saving) return;
            if (this.selectedSnippet && v.version === this.selectedSnippet.version) return;
            if (this.modalMode === 'edit' && this.isFormDirty()) {
                const ok = await this.$showModal({
                    type: 'confirm',
                    state: 'warning',
                    title: '버전 전환',
                    message: `v${this.selectedSnippet.version} 에서 수정한 내용이 저장되지 않고 사라집니다. v${v.version} 으로 바꿀까요?`,
                });
                if (!ok) return;
            }
            // 폼을 먼저 바꿔(미저장 아님) 주소 갱신이 다시 확인을 묻지 않게. 사용자 조작이라 push(뒤로가기 = 이전 버전)
            this.showDetail(v.code, Number(v.version), v);
            this.$router.push({ query: { ...this.$route.query, code: v.code, version: String(v.version) } }).catch(() => {});
        },

        fieldState(key) {
            return this.addSubmitted && this.addErrors[key] ? false : null;
        },

        async saveSnippet() {
            if (this.contentLoading || this.contentFailed) return;
            if (this.modalMode === 'add') {
                // 칸별 오류를 보여 주고 요청을 보내지 않는다
                this.addSubmitted = true;
                if (Object.keys(this.addErrors).length) return;
            } else {
                // 운영 메일이 참조하는 스니펫 — 사용처가 있거나 내용을 비우는 저장은 확인
                const { code, version, usage } = this.selectedSnippet;
                const lines = [];
                if (usage && (usage.templates || usage.parents)) {
                    lines.push(`${code} v${version} 을 직접 쓰는 템플릿 ${usage.templates}개, 이 스니펫을 포함한 상위 스니펫 ${usage.parents}개에 바로 반영됩니다(상위 스니펫을 쓰는 템플릿에도 반영).`);
                }
                if (!(this.form.content || '').trim()) {
                    lines.push('스니펫 내용이 비어 있습니다. 이 스니펫을 쓰는 모든 템플릿에서 해당 구간이 사라집니다.');
                }
                if (lines.length) {
                    const h = this.$createElement;
                    const ok = await this.$showModal({
                        type: 'confirm',
                        state: 'warning',
                        title: '스니펫 저장',
                        message: h('div', [...lines, '그래도 저장할까요?'].map(l => h('p', { class: 'mb-1 text-secondary' }, l))),
                    });
                    if (!ok) return;
                }
            }
            this.saving = true;
            try {
                if (this.modalMode === 'add') {
                    await this.$axios.post('/mail/snippet', {
                        code: this.form.code,
                        version: this.form.version,
                        label: this.form.label,
                        description: this.form.description,
                        content: this.form.content,
                    });
                } else {
                    await this.$axios.patch(
                        snippetPath(this.selectedSnippet.code, this.selectedSnippet.version),
                        {
                            label: this.form.label,
                            description: this.form.description,
                            content: this.form.content,
                        }
                    );
                }
                this.$EventBus.$emit('toast-success', '성공', '저장되었습니다.');
                await this.fetchData();
                this.closeModal();
            } catch (e) {
                const status = e.response?.status;
                if (status === 401) return;
                this.$EventBus.$emit('toast-failure', '오류', status === 409
                    ? '이미 존재하는 코드/버전입니다.'
                    : getErrorMessage(e, '저장에 실패했습니다.'));
            } finally {
                this.saving = false;
            }
        },

        async deleteSnippet() {
            const { code, version, label } = this.selectedSnippet;
            const confirmed = await this.$showModal({
                type: 'confirm',
                state: 'error',
                title: '스니펫 삭제',
                message: `${code} v${version} (${label}) 을 삭제합니다. 되돌릴 수 없습니다.`,
            });
            if (!confirmed) return;

            this.saving = true;
            try {
                await this.$axios.delete(snippetPath(code, version));
                this.$EventBus.$emit('toast-success', '성공', '삭제되었습니다.');
                this.closeModal();
                await this.fetchData();
            } catch (e) {
                const status = e.response?.status;
                if (status === 401) return;
                const templateIds = e.response?.data?.template_ids;
                if (status === 409) {
                    const msg = Array.isArray(templateIds)
                        ? `템플릿 ${templateIds.length}개가 사용 중이라 삭제할 수 없습니다.`
                        : '상위 스니펫이 포함하고 있어 삭제할 수 없습니다.';
                    this.$EventBus.$emit('toast-failure', '오류', msg);
                } else {
                    this.$EventBus.$emit('toast-failure', '오류', getErrorMessage(e, '삭제에 실패했습니다.'));
                }
            } finally {
                this.saving = false;
            }
        },

        // 확인 없이 닫는다(저장·삭제 성공, 확인을 마친 닫기, 주소에서 상세가 빠짐)
        closeModal() {
            if (!this.modalVisible) return;
            this.skipHideGuard = true;
            this.$bvModal.hide('snippet-modal');
        },

        // ESC·X·취소: 저장 중이면 막고, 저장 안 한 입력이 있으면 확인 후 닫는다
        onModalHide(bvEvt) {
            if (!this.skipHideGuard) {
                if (this.saving) {
                    bvEvt.preventDefault();
                    return;
                }
                if (this.hasUnsavedChanges) {
                    bvEvt.preventDefault();
                    this.confirmClose();
                    return;
                }
            }
            this.skipHideGuard = false;
            this.modalVisible = false;
        },

        async confirmClose() {
            if (this.hideConfirming) return;
            this.hideConfirming = true;
            const ok = await this.confirmDiscardChanges(DISCARD_DIALOG).catch(() => false);
            this.hideConfirming = false;
            if (ok) this.closeModal();
        },

        onModalHidden() {
            this.skipHideGuard = false;
            // 닫히는 사이 다시 열렸으면 새 내용을 지우지 않는다
            if (this.modalVisible) return;
            this.detailSeq++;
            this.resetSnippetForm();
            // 상세가 닫혔으니 주소에서 code·version 을 뺀다(자동 정리라 replace)
            const { code, version, ...rest } = this.$route.query;
            if (this.$route.name === 'MailSnippet' && (code !== undefined || version !== undefined)) {
                this.$router.replace({ query: rest }).catch(() => {});
            }
        },

        resetSnippetForm() {
            this.selectedSnippet = null;
            this.addSubmitted = false;
            this.form = {
                code: '',
                version: 1,
                label: '',
                description: '',
                content: '',
            };
        },
    },
};
</script>
